import {
  Image as ImageIcon,
  PlaySquare,
  Presentation,
  MessageSquareText,
} from 'lucide-react';
import { IPE_AGENT_CHAT_PATH } from './ipeRoutes';
import { userLocal } from '@/utils/ipeUserLocalState';
import i18n from '@/i18n';

/** 首页与对话页共用的 Agent 模式（文本/编程/翻译统一走 DeepSeek） */
export const AGENT_MODES = [
  {
    id: 'text',
    label: '智能对话',
    shortLabel: '对话',
    description: '文本问答、编程与翻译',
    Icon: MessageSquareText,
    default: true,
  },
  {
    id: 'image',
    label: '图像处理',
    shortLabel: '图像',
    description: '上传图片并描述处理需求',
    Icon: ImageIcon,
  },
  {
    id: 'ppt',
    label: 'PPT生成',
    shortLabel: 'PPT',
    description: '生成 PPT 大纲与页内容',
    Icon: Presentation,
  },
  {
    id: 'video',
    label: '视频创作',
    shortLabel: '视频',
    description: '图生视频 / 文生视频',
    Icon: PlaySquare,
    disabled: true,
    disabledReason: '开发中',
  },
];

/** 兼容历史会话中的 code / translate 等模式 */
const LEGACY_TEXT_MODES = new Set(['text', 'code', 'translate', 'quick']);

export const DEFAULT_AGENT_MODE = AGENT_MODES.find((m) => m.default)?.id || 'text';

export const IMAGE_AGENT_MODE = 'image';
export const VIDEO_AGENT_MODE = 'video';

export function normalizeAgentMode(modeId) {
  const raw = (modeId || '').trim().toLowerCase();
  if (!raw || LEGACY_TEXT_MODES.has(raw)) return 'text';
  return AGENT_MODES.some((m) => m.id === raw) ? raw : 'text';
}

export function getAgentModeById(modeId) {
  const normalized = normalizeAgentMode(modeId);
  return AGENT_MODES.find((m) => m.id === normalized) || AGENT_MODES[0];
}

export function isAgentModeDisabled(modeId) {
  const mode = getAgentModeById(modeId);
  return Boolean(mode?.disabled);
}

export function getAgentModeQuotaKey(modeId) {
  const normalized = normalizeAgentMode(modeId);
  if (normalized === IMAGE_AGENT_MODE) return 'image';
  if (normalized === 'ppt') return 'ppt';
  return null;
}

export function formatAgentFeatureQuotaHint(featureQuota, { monthly } = {}) {
  if (!featureQuota) return '';
  const remaining = Number(featureQuota.remaining) || 0;
  const limit = Number(featureQuota.limit) || 0;
  const isMonthly = monthly ?? Boolean(featureQuota.monthly);
  if (limit <= 0) return '';
  const unit = featureQuota.unit === 'image' || featureQuota.feature === 'image' ? '张' : '次';
  const prefix = isMonthly ? '本月剩余' : '剩余';
  return `${prefix} ${remaining}/${limit} ${unit}`;
}

export function buildAgentChatPath(conversationId) {
  if (!conversationId) return IPE_AGENT_CHAT_PATH;
  return `${IPE_AGENT_CHAT_PATH}/${encodeURIComponent(conversationId)}`;
}

export const AGENT_PENDING_CHAT_STORAGE_KEY = 'ipe.agent.pendingChat';
export const AGENT_PENDING_CHAT_LOCK_PREFIX = 'ipe.agent.pendingChat.lock.';

export function buildAgentChatUrl(conversationId) {
  const path = buildAgentChatPath(conversationId);
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

/** 读取当前登录用户的待发送草稿（按 userId 隔离，未 bind 返回 null） */
export function peekAgentPendingChat() {
  if (typeof window === 'undefined') return null;
  return userLocal.getJson(AGENT_PENDING_CHAT_STORAGE_KEY, null);
}

/** 写入当前登录用户的待发送草稿；未 bind 时写入失败，避免落入全局 key */
export function stashAgentPendingChat(payload) {
  if (typeof window === 'undefined') return false;
  return userLocal.setJson(AGENT_PENDING_CHAT_STORAGE_KEY, payload);
}

export function clearAgentPendingChat() {
  if (typeof window === 'undefined') return;
  userLocal.removeItem(AGENT_PENDING_CHAT_STORAGE_KEY);
}

export function takeAgentPendingChat() {
  const pending = peekAgentPendingChat();
  if (pending) {
    clearAgentPendingChat();
  }
  return pending;
}

/**
 * 退出 / 换号：清当前用户 pending，并丢弃旧全局 key / lock 前缀（只删不迁）。
 */
export function clearAgentPendingChatForLogout() {
  clearAgentPendingChat();
  userLocal.dropLegacyKeys(AGENT_PENDING_CHAT_STORAGE_KEY);
  userLocal.dropLegacyKeyPrefix(AGENT_PENDING_CHAT_LOCK_PREFIX);
}

/** 防重复消费锁（按用户隔离；未 bind 时拒绝，避免跨账号抢锁） */
export function acquireAgentPendingChatLock(pendingKey) {
  if (!pendingKey || typeof window === 'undefined') return true;
  const name = `${AGENT_PENDING_CHAT_LOCK_PREFIX}${pendingKey}`;
  const now = Date.now();
  const existing = userLocal.getItem(name);
  if (existing) {
    const started = Number(existing);
    if (Number.isFinite(started) && now - started < 10 * 60 * 1000) {
      return false;
    }
  }
  return userLocal.setItem(name, String(now));
}

export function releaseAgentPendingChatLock(pendingKey) {
  if (!pendingKey || typeof window === 'undefined') return;
  userLocal.removeItem(`${AGENT_PENDING_CHAT_LOCK_PREFIX}${pendingKey}`);
}

export function openAgentChatWindow({
  mode,
  message,
  attachmentsJson,
  imageParamsJson,
  videoParamsJson,
  webSearchEnabled = true,
} = {}) {
  stashAgentPendingChat({
    mode,
    message,
    attachmentsJson,
    imageParamsJson,
    videoParamsJson,
    webSearchEnabled: Boolean(webSearchEnabled),
    createdAt: Date.now(),
  });
  window.open(buildAgentChatUrl(), '_blank', 'noopener,noreferrer');
}

export function openAgentConversationWindow(conversationId) {
  if (!conversationId || typeof window === 'undefined') return;
  window.open(buildAgentChatUrl(conversationId), '_blank', 'noopener,noreferrer');
}

export function getAgentPlaceholder(modeId) {
  const mode = normalizeAgentMode(modeId);
  const key = mode === IMAGE_AGENT_MODE
    ? 'image'
    : mode === 'ppt'
      ? 'ppt'
      : mode === 'video'
        ? 'video'
        : 'text';
  const fallback = {
    image: '输入您的灵感…',
    ppt: '描述演示主题、页数和风格要求…',
    video: '描述视频场景，或上传首帧图片进行图生视频…',
    text: '请输入提示词进行提问，Enter 发送,Shift+Enter换行',
  }[key];
  if (!i18n.isInitialized) return fallback;
  return i18n.t(`agentHome.placeholder.${key}`, { ns: 'workbench', defaultValue: fallback });
}
